<script>
import { mapGetters } from 'vuex';
import { frontendURL } from '../../helper/URLHelper';
import { fireConfetti } from '../../helper/confetti';
import { renderMarkdown } from '../../helper/markdownHelper';

const DISMISSED_STORAGE_KEY = 'dismissedReleaseNotes';

export default {
  name: 'ReleaseNotesModal',

  data() {
    return {
      visible: false,
      todayNote: null,
      cancelConfetti: null,
      sections: [
        {
          key: 'summary_new_features',
          label: 'RELEASE_NOTES.SECTIONS.NEW_FEATURES',
        },
        { key: 'summary_additions', label: 'RELEASE_NOTES.SECTIONS.ADDITIONS' },
        { key: 'summary_fixes', label: 'RELEASE_NOTES.SECTIONS.FIXES' },
      ],
    };
  },

  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),

    todayDate() {
      if (!this.todayNote?.note_date) return '';
      return this.formatNoteDate(this.todayNote.note_date);
    },
  },

  mounted() {
    this.fetchTodayNote();
  },

  beforeDestroy() {
    // Se o componente sair antes da animação acabar, remove o canvas.
    if (this.cancelConfetti) this.cancelConfetti();
  },

  methods: {
    hasItems(key) {
      return !!this.todayNote?.[`${key}_markdown`];
    },

    sectionHtml(key) {
      return renderMarkdown(this.todayNote?.[`${key}_markdown`]);
    },

    formatNoteDate(dateStr) {
      // note_date vem como YYYY-MM-DD; `new Date` trataria isso como UTC e
      // deslocaria o dia em fusos negativos, por isso montamos a data local.
      const [year, month, day] = dateStr.split('-').map(Number);
      // O locale do i18n é `pt_BR`, que não é uma tag BCP 47 válida.
      const locale =
        String(this.$i18n?.locale || '').replace('_', '-') || undefined;
      return new Date(year, month - 1, day).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    readDismissed() {
      try {
        const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    async fetchTodayNote() {
      const note = await this.$store.dispatch('releaseNotes/fetchToday');
      if (!note) return;
      if (this.readDismissed().includes(note.id)) return;

      this.todayNote = note;
      this.visible = true;
      // Espera o modal pintar para o confete cair sobre ele.
      this.$nextTick(() => {
        this.cancelConfetti = fireConfetti();
      });
    },

    dismiss() {
      if (this.cancelConfetti) {
        this.cancelConfetti();
        this.cancelConfetti = null;
      }
      if (this.todayNote) {
        const dismissed = this.readDismissed();
        dismissed.push(this.todayNote.id);
        try {
          window.localStorage.setItem(
            DISMISSED_STORAGE_KEY,
            JSON.stringify(dismissed)
          );
        } catch (error) {
          // localStorage indisponível (modo privado / cota) — apenas fecha.
        }
      }
      this.visible = false;
    },

    viewAll() {
      this.dismiss();
      this.$router.push(
        frontendURL(
          `accounts/${this.currentAccountId}/settings/release_notes/list`
        )
      );
    },
  },
};
</script>

<template>
  <woot-modal v-if="visible" :show="visible" :on-close="dismiss">
    <woot-modal-header
      :header-title="todayNote.title"
      :header-content="todayDate"
    />

    <div class="px-8 py-4 overflow-y-auto max-h-[50vh]">
      <div
        v-for="section in sections"
        v-show="hasItems(section.key)"
        :key="section.key"
        class="mb-4"
      >
        <h3 class="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
          {{ $t(section.label) }}
        </h3>
        <div
          v-dompurify-html="sectionHtml(section.key)"
          class="max-w-none prose prose-sm dark:prose-invert prose-slate prose-code:before:content-none prose-code:after:content-none [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none"
        />
      </div>
    </div>

    <div class="flex flex-row justify-end w-full gap-2 px-6 py-4">
      <woot-button variant="clear" @click="dismiss">
        {{ $t('RELEASE_NOTES.MODAL.DISMISS') }}
      </woot-button>
      <woot-button @click="viewAll">
        {{ $t('RELEASE_NOTES.MODAL.VIEW_ALL') }}
      </woot-button>
    </div>
  </woot-modal>
</template>
